import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrgs } from '@/contexts/OrgsContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useGetOrganizationProductEntitlements } from '@/api/auth/product-entitlements/product-entitlements';
import { postListProfiles } from '@/api/control/control-plane/control-plane';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { cn } from '@/lib/utils';

interface EntitlementsCardProps {
  className?: string;
}

/**
 * EntitlementsCard component displays organization overview information.
 * Polls for entitlements if none exist yet (background process creating default entitlement).
 */
export function EntitlementsCard({ className }: EntitlementsCardProps) {
  const { selectedOrganizationId } = useOrgs();
  const { t } = useLanguage();
  const [pollingInterval, setPollingInterval] = useState<number | false>(1000);
  const [_pollAttempts, setPollAttempts] = useState<number>(0);

  const { data: entitlementsResponse, isLoading: isLoadingEntitlements, error: entitlementsError, refetch: refetchEntitlements } =
    useGetOrganizationProductEntitlements(selectedOrganizationId ?? '', 'data-mission', undefined, {
      query: {
        refetchInterval: pollingInterval,
        enabled: !!selectedOrganizationId,
      },
    });

  const entitlements = entitlementsResponse?.data;

  // Fetch profiles list to count active profiles
  const { data: profilesData, isLoading: isLoadingProfiles, error: profilesError, refetch: refetchProfiles } = useQuery({
    queryKey: ['profiles', 'list', selectedOrganizationId],
    queryFn: async () => {
      const response = await postListProfiles(selectedOrganizationId ?? '', { pageSize: 100 });
      return response;
    },
    enabled: !!selectedOrganizationId,
    retry: 1,
  });

  const activeProfilesCount =
    profilesData?.data.items?.filter((profile) => profile.isEnabled).length ?? 0;

  // Stop polling once we receive a valid response (with or without entitlements)
  // The polling is only for waiting for the backend to create a default entitlement
  useEffect(() => {
    if (entitlementsResponse?.data) {
      // We got a valid response - stop polling
      setPollingInterval(false);
    } else if (pollingInterval !== false) {
      setPollAttempts((prev) => {
        const newAttempts = prev + 1;
        if (newAttempts >= 10) {
          setPollingInterval(false);
        }
        return newAttempts;
      });
    }
  }, [entitlementsResponse?.data, pollingInterval]);

  // Show error states
  if (entitlementsError && pollingInterval === false) {
    return (
      <Card className={cn("flex-shrink-0 w-fit min-w-[400px]", className)}>
        <CardHeader className="pb-3">
          <CardTitle>{t.dashboard.entitlements.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryErrorState
            error={entitlementsError}
            message={t.errors.entitlements.failedToLoadEntitlements}
            onRetry={() => void refetchEntitlements()}
          />
        </CardContent>
      </Card>
    );
  }

  if (profilesError) {
    return (
      <Card className={cn("flex-shrink-0 w-fit min-w-[400px]", className)}>
        <CardHeader className="pb-3">
          <CardTitle>{t.dashboard.entitlements.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <QueryErrorState
            error={profilesError}
            message={t.errors.profiles.failedToLoadProfiles}
            onRetry={() => void refetchProfiles()}
          />
        </CardContent>
      </Card>
    );
  }

  // Check if using default entitlement that hasn't expired (treat as trial for display purposes)
  const now = new Date();
  const hasActiveDefaultEntitlement = entitlements?.entitlements.some((e) => {
    if (!e.isDefault) return false;
    if (!e.expirationDate) return true; // No expiration means it's always active
    return new Date(e.expirationDate) > now;
  }) ?? false;
  const showAsActiveTrial = entitlements?.hasActiveTrial ?? hasActiveDefaultEntitlement;

  return (
    <Card className={cn("flex-shrink-0 w-fit min-w-[400px]", className)}>
      <CardHeader className="pb-3">
        <CardTitle>{t.dashboard.entitlements.title}</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoadingEntitlements || isLoadingProfiles || !entitlements ? (
          <div className="flex flex-wrap gap-6 items-start">
            <div className="flex-shrink-0">
              <Skeleton className="h-3 w-16 mb-2" />
              <Skeleton className="h-8 w-24 mb-1" />
              <Skeleton className="h-3 w-28" />
            </div>
            <div className="h-12 w-px bg-border hidden md:block" />
            <div className="min-w-[200px]">
              <div className="flex gap-6 items-start">
                <div>
                  <Skeleton className="h-3 w-16 mb-2" />
                  <Skeleton className="h-6 w-20" />
                </div>
                <div>
                  <Skeleton className="h-3 w-16 mb-2" />
                  <Skeleton className="h-6 w-24" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-6 items-start">
            <div className="flex-shrink-0">
              <p className="text-xs font-medium text-muted-foreground mb-1">{t.dashboard.entitlements.profiles}</p>
              <p className="text-2xl font-bold">
                <span
                  className={activeProfilesCount > entitlements.count ? 'text-error' : ''}
                >
                  {activeProfilesCount}
                </span>
                <span className="text-muted-foreground"> / {entitlements.count}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">{t.dashboard.entitlements.activeAvailable}</p>
            </div>
            <div className="h-12 w-px bg-border hidden md:block" />
            <div className="min-w-[200px]">
              <div className="flex gap-6 items-start">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t.dashboard.entitlements.freeTrial}</p>
                  <p className="text-xl font-bold">
                    {showAsActiveTrial ? (
                      <span className="text-success">{t.dashboard.entitlements.active}</span>
                    ) : (
                      <span className="text-muted-foreground">{t.dashboard.entitlements.inactive}</span>
                    )}
                  </p>
                </div>
                {!entitlements.isPerpetual && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">{t.dashboard.entitlements.expires}</p>
                    <p className="text-xl font-bold">
                      {entitlements.expiresAt
                        ? new Date(entitlements.expiresAt).toLocaleDateString()
                        : t.dashboard.entitlements.notAvailable}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
